import type { ReactNode } from 'react'
import { useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { getHashPath, navigate } from '../router/hash'
import { PRIMARY_NAV, SECONDARY_NAV, type NavItem } from '../data/nav'
import { Icon } from '../components/Icon'

type SearchProps = {
  value: string
  onInput: (value: string) => void
  placeholder?: string
}

type Props = {
  currentPath: string
  title: string
  subtitle?: string
  actions?: ReactNode
  search?: SearchProps
  flush?: boolean
  children: ReactNode
}

function isActive(item: NavItem, path: string): boolean {
  if (item.path === '/clients') return path === '/clients' || path.startsWith('/clients/')
  return path === item.path
}

function NavLink({ item, path }: { item: NavItem; path: string }) {
  const active = isActive(item, path)
  return (
    <a
      className={`nav-item${active ? ' is-active' : ''}`}
      href={`#${item.path}`}
      title={item.label}
      onClick={(e) => {
        e.preventDefault()
        navigate(item.path)
      }}
    >
      <Icon name={item.icon} />
      <span className="nav-item__label">{item.label}</span>
    </a>
  )
}

export function AppShell({
  currentPath,
  title,
  subtitle,
  actions,
  search,
  flush,
  children,
}: Props) {
  const { user, logout } = useAuth()
  const [collapsed, setCollapsed] = useState(false)
  const path = currentPath || getHashPath()
  const displayName = user?.displayName || 'Care Team'
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <div className={`shell${collapsed ? ' is-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar__brand">
          <div className="sidebar__logo">M</div>
          <div className="sidebar__title">
            <b>MyADC</b>
            <span>Adult Day Care</span>
          </div>
        </div>

        <nav className="sidebar__nav">
          <div className="sidebar__group-label sidebar__group-label--first">Navigation</div>
          {PRIMARY_NAV.map((item) => (
            <NavLink key={item.id} item={item} path={path} />
          ))}
          <div className="sidebar__group-label">Operations</div>
          {SECONDARY_NAV.map((item) => (
            <NavLink key={item.id} item={item} path={path} />
          ))}
        </nav>

        <div className="sidebar__foot">
          <div className="sidebar-user">
            <span className="sidebar-user__avatar">{initials || 'CT'}</span>
            <span className="sidebar-user__meta nav-item__label">
              <b>{displayName}</b>
              <small>Care team</small>
            </span>
          </div>
          <button type="button" className="sidebar-signout" onClick={logout} title="Sign out">
            <Icon name="logout" />
            <span className="nav-item__label">Sign out</span>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn"
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            onClick={() => setCollapsed((v) => !v)}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} />
          </button>

          <div className="topbar__title">
            <h1>{title}</h1>
            {subtitle ? <span>{subtitle}</span> : null}
          </div>

          <div className="topbar__spacer" />

          {search ? (
            <label className="searchbox">
              <Icon name="search" />
              <input
                type="search"
                value={search.value}
                placeholder={search.placeholder || 'Search…'}
                onInput={(e) => search.onInput((e.target as HTMLInputElement).value)}
              />
            </label>
          ) : null}

          {actions ? <div className="topbar__actions">{actions}</div> : null}

          <button type="button" className="icon-btn" title="Notifications">
            <Icon name="bell" />
          </button>

          <div className="userchip" title={displayName}>
            <span className="avatar avatar--topbar">
              {initials || 'CT'}
            </span>
            <span className="userchip__name">
              <b>{displayName}</b>
              <span>Signed in</span>
            </span>
          </div>
        </header>

        <main className={`content${flush ? ' content--flush' : ''}`}>{children}</main>
      </div>
    </div>
  )
}
